// context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  getAuth, 
  onAuthStateChanged, 
  signOut as firebaseSignOut 
} from 'firebase/auth';
import axios from 'axios';
import { toast } from 'react-toastify';
import { auth } from '../firebaseConfig';
import { CONSTANTS } from '../constants/Constants';

// Create context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Provider component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sign out function
  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      // Clear stored user data
      localStorage.removeItem('user');
      sessionStorage.removeItem('user');
      // Reset state
      setUserDetails(null);
      toast.success('Successfully signed out');
    } catch (error) {
      console.error('Sign out error:', error);
      toast.error('Failed to sign out');
      throw error;
    }
  };

  // Update user data in MongoDB and local state
  const updateUserData = async (userData) => {
    try {
      if (!currentUser || !currentUser.uid) return;
      
      const response = await axios.put(`${CONSTANTS.APIURL}/api/users/${currentUser.uid}`, userData);
      
      // Update local storage
      const storedUser = JSON.parse(localStorage.getItem('user') || sessionStorage.getItem('user') || '{}');
      const updatedUser = { ...storedUser, ...response.data };
      
      if (localStorage.getItem('user')) {
        localStorage.setItem('user', JSON.stringify(updatedUser));
      } else {
        sessionStorage.setItem('user', JSON.stringify(updatedUser));
      }
      
      // Update state
      setUserDetails(response.data);
      
      return response.data;
    } catch (error) {
      console.error('Update user data error:', error);
      throw error;
    }
  };

  // Fetch user details from MongoDB
  const fetchUserDetails = async (uid) => {
    try {
      const response = await axios.get(`${CONSTANTS.APIURL}/api/users/${uid}`);
      setUserDetails(response.data);
      return response.data;
    } catch (error) {
      console.error('Fetch user details error:', error);
      if (error.response && error.response.status === 404) {
        return null; // User doesn't exist in MongoDB yet
      }
      throw error;
    }
  };

  useEffect(() => {
    // Get saved user from localStorage or sessionStorage
    const savedUser = localStorage.getItem('user') || sessionStorage.getItem('user');
    if (savedUser) {
      const userData = JSON.parse(savedUser);
      setUserDetails(userData);
    }

    // Subscribe to auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        try {
          // Get user details from MongoDB
          await fetchUserDetails(user.uid);
        } catch (error) {
          console.error('Failed to fetch user details:', error);
        }
      }
      
      setLoading(false);
    });

    return unsubscribe; // Unsubscribe on cleanup
  }, [auth]);

  // Context value
  const value = {
    currentUser,
    userDetails,
    loading,
    signOut,
    updateUserData,
    fetchUserDetails
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Export the default AuthContext
export default AuthProvider;